import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';

// SY Brand colors
const SY_NAVY = rgb(10 / 255, 25 / 255, 41 / 255);
const SY_TEAL = rgb(32 / 255, 201 / 255, 151 / 255);
const WHITE = rgb(1, 1, 1);

const DISCLAIMER = 'Charlie is not a medical device. All suspected concussions should be assessed by a qualified medical professional.';

// Convert data URL to Uint8Array for pdf-lib
const dataUrlToBytes = (dataUrl: string): Uint8Array => {
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// A4 Poster: 210 x 297mm → 595.28 x 841.89 points
export const generateA4Poster = async (
  qrDataUrl: string,
  venueName: string,
  qrUrl: string
) => {
  const doc = await PDFDocument.create();
  const page = doc.addPage([595.28, 841.89]);
  const font = await doc.embedFont(StandardFonts.HelveticaBold);
  const fontRegular = await doc.embedFont(StandardFonts.Helvetica);

  // Navy background
  page.drawRectangle({
    x: 0, y: 0,
    width: 595.28, height: 841.89,
    color: SY_NAVY,
  });

  // Heading
  const heading = 'Scan for Concussion Help';
  const headingSize = 28;
  const headingWidth = font.widthOfTextAtSize(heading, headingSize);
  page.drawText(heading, {
    x: (595.28 - headingWidth) / 2,
    y: 780,
    size: headingSize,
    font,
    color: WHITE,
  });

  // Teal accent line
  page.drawRectangle({
    x: 200, y: 760,
    width: 195.28, height: 3,
    color: SY_TEAL,
  });

  // QR code image (~150mm = 425 points)
  const qrBytes = dataUrlToBytes(qrDataUrl);
  const qrImage = await doc.embedPng(qrBytes);
  const qrSize = 425;
  const qrX = (595.28 - qrSize) / 2;
  page.drawImage(qrImage, {
    x: qrX, y: 290,
    width: qrSize, height: qrSize,
  });

  // White background behind QR for scanability
  page.drawRectangle({
    x: qrX - 10, y: 280,
    width: qrSize + 20, height: qrSize + 20,
    color: WHITE,
    borderColor: SY_TEAL,
    borderWidth: 3,
  });

  // Draw QR on top
  page.drawImage(qrImage, {
    x: qrX, y: 290,
    width: qrSize, height: qrSize,
  });

  // Venue name
  const nameSize = 22;
  const nameWidth = font.widthOfTextAtSize(venueName, nameSize);
  page.drawText(venueName, {
    x: (595.28 - nameWidth) / 2,
    y: 240,
    size: nameSize,
    font,
    color: SY_TEAL,
  });

  // URL
  const urlSize = 10;
  const urlWidth = fontRegular.widthOfTextAtSize(qrUrl, urlSize);
  page.drawText(qrUrl, {
    x: (595.28 - urlWidth) / 2,
    y: 215,
    size: urlSize,
    font: fontRegular,
    color: WHITE,
  });

  // Disclaimer
  const disclaimerSize = 7;
  const disclaimerWidth = fontRegular.widthOfTextAtSize(DISCLAIMER, disclaimerSize);
  page.drawText(DISCLAIMER, {
    x: (595.28 - disclaimerWidth) / 2,
    y: 30,
    size: disclaimerSize,
    font: fontRegular,
    color: rgb(1, 1, 1),
    opacity: 0.5,
  });

  // Charlie branding
  const brandText = 'Powered by Charlie — Safeguarding You';
  const brandWidth = fontRegular.widthOfTextAtSize(brandText, 9);
  page.drawText(brandText, {
    x: (595.28 - brandWidth) / 2,
    y: 50,
    size: 9,
    font: fontRegular,
    color: SY_TEAL,
  });

  const pdfBytes = await doc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  saveAs(blob, `charlie-poster-${venueName.replace(/\s+/g, '-').toLowerCase()}.pdf`);
};

// Sticker: 100 x 100mm → 283.46 x 283.46 points
export const generateSticker = async (
  qrDataUrl: string,
  venueName: string,
  qrUrl: string
) => {
  const doc = await PDFDocument.create();
  const size = 283.46;
  const page = doc.addPage([size, size]);
  const font = await doc.embedFont(StandardFonts.HelveticaBold);
  const fontRegular = await doc.embedFont(StandardFonts.Helvetica);

  // White background
  page.drawRectangle({
    x: 0, y: 0,
    width: size, height: size,
    color: WHITE,
  });

  // Teal border
  page.drawRectangle({
    x: 2, y: 2,
    width: size - 4, height: size - 4,
    borderColor: SY_TEAL,
    borderWidth: 2,
  });

  // QR code (~80mm = 226 points)
  const qrBytes = dataUrlToBytes(qrDataUrl);
  const qrImage = await doc.embedPng(qrBytes);
  const qrSize = 200;
  const qrX = (size - qrSize) / 2;
  page.drawImage(qrImage, {
    x: qrX, y: 50,
    width: qrSize, height: qrSize,
  });

  // Venue name at top
  const nameSize = 10;
  const truncatedName = venueName.length > 30 ? venueName.substring(0, 27) + '...' : venueName;
  const nameWidth = font.widthOfTextAtSize(truncatedName, nameSize);
  page.drawText(truncatedName, {
    x: (size - nameWidth) / 2,
    y: 260,
    size: nameSize,
    font,
    color: SY_NAVY,
  });

  // URL at bottom
  const urlSize = 6;
  const shortUrl = qrUrl.replace(/^https?:\/\//, '');
  const urlWidth = fontRegular.widthOfTextAtSize(shortUrl, urlSize);
  page.drawText(shortUrl, {
    x: (size - urlWidth) / 2,
    y: 30,
    size: urlSize,
    font: fontRegular,
    color: rgb(0.5, 0.5, 0.5),
  });

  // "Scan for Help" label
  const label = 'Scan for Concussion Help';
  const labelSize = 8;
  const labelWidth = fontRegular.widthOfTextAtSize(label, labelSize);
  page.drawText(label, {
    x: (size - labelWidth) / 2,
    y: 15,
    size: labelSize,
    font: fontRegular,
    color: SY_NAVY,
  });

  const pdfBytes = await doc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  saveAs(blob, `charlie-sticker-${venueName.replace(/\s+/g, '-').toLowerCase()}.pdf`);
};

// Digital PNG: 1080 x 1080px
export const generateDigitalPng = async (
  qrDataUrl: string,
  venueName: string
) => {
  const canvas = document.createElement('canvas');
  const size = 1080;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Navy background
  ctx.fillStyle = '#0a1929';
  ctx.fillRect(0, 0, size, size);

  // Teal border
  ctx.strokeStyle = '#20c997';
  ctx.lineWidth = 6;
  ctx.strokeRect(20, 20, size - 40, size - 40);

  // Heading
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 42px Helvetica, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Scan for Concussion Help', size / 2, 120);

  // Teal line
  ctx.strokeStyle = '#20c997';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(340, 140);
  ctx.lineTo(740, 140);
  ctx.stroke();

  // QR code
  const qrImage = new Image();
  await new Promise<void>((resolve) => {
    qrImage.onload = () => resolve();
    qrImage.src = qrDataUrl;
  });

  // White background for QR
  const qrSize = 600;
  const qrX = (size - qrSize) / 2;
  const qrY = 190;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);

  // Teal border around QR
  ctx.strokeStyle = '#20c997';
  ctx.lineWidth = 4;
  ctx.strokeRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);

  ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

  // Venue name
  ctx.fillStyle = '#20c997';
  ctx.font = 'bold 36px Helvetica, Arial, sans-serif';
  ctx.fillText(venueName, size / 2, 860);

  // Charlie branding
  ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
  ctx.font = '18px Helvetica, Arial, sans-serif';
  ctx.fillText('Powered by Charlie — Safeguarding You', size / 2, 920);

  // Disclaimer
  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.font = '14px Helvetica, Arial, sans-serif';
  ctx.fillText(DISCLAIMER, size / 2, 1040);

  canvas.toBlob((blob) => {
    if (blob) {
      saveAs(blob, `charlie-qr-${venueName.replace(/\s+/g, '-').toLowerCase()}.png`);
    }
  }, 'image/png');
};
